import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import { AllocationVariantOverrideVisibility } from '@/components/AllocationSelectionPanel/AllocationVariantVisibilityOverride.vue';
import {
    GetEventsInTimeRangeQuery,
    GetExternalEventsInTimeRangeQuery,
    GetProjectsInTimeRangeQuery,
} from '@/generated/graphql';
import { DateTime } from 'luxon';
import { assign, setup } from 'xstate';
import { Section } from './AllocationDesignerSelectionPanel.vue';
import {
    createSectionsFromFilterData,
    getChangedStatesFromSections,
    getDefaultStatesFromSections,
    makeActiveVariantsVisible,
    makeMainVariantVisible,
    toggleNonActiveVariants,
    toggleSectionIsExpanded,
    updateChangedStateOnVisibilityOverride,
    updateChangedStatesOnToggle,
    updateSectionsOnToggle,
    updateSectionsVisibility,
    updateSectionsVisibilityOverride,
} from './composables/useDesignerFilterData';

export type VariantStateVisibility = 'Visible' | 'Invisible';

export type VariantState = {
    id: string;
    variantId: string;
    variantType: AllocationGroupType;
    visibility: VariantStateVisibility;
    visibilityOverride?: VariantStateVisibility;
};

export type FilterContext = {
    type: AllocationGroupType;
    activeGroupStates: string[];
    data: any;
    sections: Section[];
    status: string;
    error: boolean;
    defaultStates: VariantState[];
    scenarioStates: VariantState[];
    changedStates: VariantState[];
};

export type FilterMachineEvents =
    | { type: 'toggle.section.expand'; sectionId: string }
    | {
          type: 'toggle.variant.visibility';
          toggleValue: VariantStateVisibility;
          sectionId: string;
          variantId: string;
      }
    | {
          type: 'toggle.variant.visibilityOverride';
          toggleValue: AllocationVariantOverrideVisibility;
          sectionId: string;
          variantId: string;
      }
    | { type: 'undo.changes'; start: DateTime; end: DateTime }
    | { type: 'make.mainvariant.visible' }
    | { type: 'make.activevariant.visible' }
    | { type: 'toggle.nonactive.variants'; sectionId: string }
    | {
          type: 'set.data';
          data: GetEventsInTimeRangeQuery | GetProjectsInTimeRangeQuery | GetExternalEventsInTimeRangeQuery;
          start: DateTime;
          end: DateTime;
      }
    | { type: 'set.scenario.visibilities'; visibilities: VariantState[] };

export const createFilterMachine = (machineId: string) => {
    return setup({
        types: {} as {
            input: {
                type: AllocationGroupType;
                activeGroupStates: string[];
            };
            context: FilterContext;
            events: FilterMachineEvents;
        },
    }).createMachine({
        id: machineId,
        context: ({ input }) => ({
            type: input.type,
            activeGroupStates: input.activeGroupStates,
            data: undefined,
            sections: [],
            status: 'No-Changes',
            error: false,
            defaultStates: [],
            scenarioStates: [],
            changedStates: [],
        }),
        initial: 'loading',
        states: {
            loading: {},
            loaded: {
                on: {
                    'toggle.section.expand': {
                        actions: [
                            assign({
                                sections: ({ context, event }) =>
                                    toggleSectionIsExpanded(context.sections, event.sectionId),
                            }),
                        ],
                    },
                    'toggle.variant.visibility': {
                        actions: assign(({ context, event }) => {
                            const defaultState = context.defaultStates.find(
                                (state) => state.variantId === event.variantId,
                            );
                            const sections = updateSectionsOnToggle(
                                context.sections,
                                event.toggleValue,
                                event.sectionId,
                                event.variantId,
                            );
                            let changedStates = context.changedStates;
                            if (defaultState) {
                                changedStates = updateChangedStatesOnToggle(
                                    event.variantId,
                                    event.toggleValue,
                                    defaultState,
                                    context.changedStates,
                                );
                            }
                            return { sections, changedStates };
                        }),
                    },
                    'toggle.variant.visibilityOverride': {
                        actions: assign(({ context, event }) => {
                            const variantId = event.variantId;
                            const defaultState = context.defaultStates.find(
                                (state) => state.variantId === event.variantId,
                            );
                            const sections = updateSectionsVisibilityOverride(
                                context.sections,
                                event.toggleValue,
                                event.sectionId,
                                variantId,
                            );
                            let changedStates = context.changedStates;
                            if (defaultState) {
                                changedStates = updateChangedStateOnVisibilityOverride(
                                    variantId,
                                    defaultState,
                                    context.changedStates,
                                    event.toggleValue,
                                );
                            }
                            return { sections, changedStates };
                        }),
                    },
                    'undo.changes': {
                        actions: assign(({ context, event }) => {
                            // recreate sections
                            const sections = createSectionsFromFilterData(
                                context.activeGroupStates,
                                context.type,
                                context.data,
                                event.start,
                                event.end,
                            );
                            // calc defaultStates
                            const defaultStates = getDefaultStatesFromSections(sections, context.type);
                            return {
                                ...context,
                                sections,
                                changedStates: [],
                                defaultStates,
                            };
                        }),
                    },
                    'make.mainvariant.visible': {
                        actions: assign(({ context }) => {
                            const updatedSections = makeMainVariantVisible(context.sections, context.activeGroupStates);
                            const updatedChangedStates = getChangedStatesFromSections(
                                updatedSections,
                                context.type,
                                context.defaultStates,
                                context.scenarioStates,
                            );
                            return { ...context, sections: updatedSections, changedStates: updatedChangedStates };
                        }),
                    },
                    'make.activevariant.visible': {
                        actions: assign(({ context }) => {
                            const updatedSections = makeActiveVariantsVisible(
                                context.sections,
                                context.activeGroupStates,
                            );
                            const updatedChangedStates = getChangedStatesFromSections(
                                updatedSections,
                                context.type,
                                context.defaultStates,
                                context.scenarioStates,
                            );
                            return { ...context, sections: updatedSections, changedStates: updatedChangedStates };
                        }),
                    },
                    'toggle.nonactive.variants': {
                        actions: assign(({ context, event }) => {
                            const updatedSections = toggleNonActiveVariants(event.sectionId, context.sections);
                            const updatedChangedStates = getChangedStatesFromSections(
                                updatedSections,
                                context.type,
                                context.defaultStates,
                                context.scenarioStates,
                            );
                            return { ...context, sections: updatedSections, changedStates: updatedChangedStates };
                        }),
                    },
                    'set.scenario.visibilities': {
                        actions: [
                            assign(({ context, event }) => {
                                const updatedSections = updateSectionsVisibility(context.sections, event.visibilities);
                                const defaultStates = getDefaultStatesFromSections(updatedSections, context.type); // necessary?
                                return {
                                    sections: updatedSections,
                                    scenarioStates: event.visibilities,
                                    changedStates: [],
                                    defaultStates,
                                };
                            }),
                        ],
                    },
                },
            },
        },
        on: {
            'set.data': {
                actions: [
                    assign(({ context, event }) => {
                        const sections = createSectionsFromFilterData(
                            context.activeGroupStates,
                            context.type,
                            event.data,
                            event.start,
                            event.end,
                        );
                        const defaultStates = getDefaultStatesFromSections(sections, context.type);
                        return {
                            data: event.data,
                            start: event.start,
                            end: event.end,
                            sections,
                            defaultStates,
                        };
                    }),
                ],
                target: '.loaded',
            },
        },
    });
};

export type FilterMachineType = ReturnType<typeof createFilterMachine>;
